import React, { useState, useEffect } from "react";
import { FiSettings, FiMessageCircle, FiCalendar, FiClock, FiCheckCircle, FiInfo } from "react-icons/fi";
import { listConfiguracion, createConfiguracion, updateConfiguracion } from "../api/odontologia";
import { showAlertError, showAlertExito } from "../utils/alertHelper";
import Loader from "./Loader";

const ConfiguracionOdontologia = () => {
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [config, setConfig] = useState(null);
    const [form, setForm] = useState({
        mensajes: 1,
        calendarios_cantidad: 1,
        tiempo_calendarios: 20
    });

    const id_sucursal = sessionStorage.getItem("id_sucursal");

    useEffect(() => {
        fetchConfig();
    }, []);

    const fetchConfig = async () => {
        setLoading(true);
        try {
            const res = await listConfiguracion();
            if (res && res.estado === 1 && res.data) {
                setConfig(res.data);
                setForm({
                    mensajes: res.data.mensajes,
                    calendarios_cantidad: res.data.calendarios_cantidad,
                    tiempo_calendarios: res.data.tiempo_calendarios
                });
            }
        } catch (error) {
            console.error("Error fetching config:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ 
            ...prev, 
            [name]: name === "mensajes" ? parseInt(value) : parseInt(value) || 0 
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);

        try {
            const payload = {
                ...form,
                id_sucursal: parseInt(id_sucursal)
            };

            let res;
            if (config && config.id) {
                res = await updateConfiguracion(config.id, payload);
            } else {
                res = await createConfiguracion(payload);
            }

            if (res && res.estado === 1) {
                // Refresh config state
                if (res.data) setConfig(res.data);
                else fetchConfig(); 
            }
        } catch (error) {
            console.error("Error saving config:", error);
            showAlertError("Error", "No se pudo guardar la configuración");
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) return <Loader />;

    return (
        <div className="p-6 max-w-4xl mx-auto animate-fade-in">
            <div className="flex items-center gap-4 mb-8">
                <div className="bg-blue-600 p-3 rounded-2xl shadow-lg shadow-blue-200">
                    <FiSettings className="text-white w-6 h-6" />
                </div>
                <div>
                    <h1 className="text-2xl font-bold text-slate-800">Configuración de Odontología</h1>
                    <p className="text-slate-500 text-sm">Gestiona los parámetros de agenda y notificaciones automáticas</p>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Sección 1: Notificaciones */}
                <section className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100 transition-all hover:shadow-md">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="bg-emerald-50 p-2 rounded-xl">
                            <FiMessageCircle className="text-emerald-600 w-5 h-5" />
                        </div>
                        <h2 className="text-lg font-bold text-slate-800">Mensajería Automática</h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                        <div>
                            <p className="text-slate-600 text-sm mb-4 leading-relaxed">
                                Habilita el envío automático de recordatorios y confirmaciones de citas a través de WhatsApp.
                            </p>
                            <div className="flex items-center gap-2 text-[10px] font-bold text-slate-400 uppercase tracking-widest bg-slate-50 px-3 py-1.5 rounded-lg w-fit">
                                <FiInfo size={12} /> Requiere módulo de WS activo
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="text-xs font-bold text-slate-500 uppercase ml-1">Estado de Mensajes</label>
                            <select
                                name="mensajes"
                                value={form.mensajes}
                                onChange={handleInputChange}
                                className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-5 py-3.5 text-slate-700 font-medium focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500 outline-none transition-all appearance-none cursor-pointer"
                            >
                                <option value={1}>Habilitado (Enviar Automáticamente)</option>
                                <option value={0}>Deshabilitado (Manual)</option>
                            </select>
                        </div>
                    </div>
                </section>

                {/* Sección 2: Gestión de Agenda */}
                <section className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100 transition-all hover:shadow-md">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="bg-indigo-50 p-2 rounded-xl">
                            <FiCalendar className="text-indigo-600 w-5 h-5" />
                        </div>
                        <h2 className="text-lg font-bold text-slate-800">Parámetros de la Agenda</h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div className="space-y-2">
                            <label className="flex items-center gap-2 text-xs font-bold text-slate-500 uppercase ml-1">
                                <FiCalendar className="text-slate-400" /> Cantidad de Calendarios
                            </label>
                            <div className="relative group">
                                <input
                                    type="number"
                                    name="calendarios_cantidad"
                                    min="1"
                                    max="10"
                                    value={form.calendarios_cantidad}
                                    onChange={handleInputChange}
                                    className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-5 py-3.5 text-slate-700 font-medium focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all"
                                    placeholder="Ej. 2"
                                />
                                <div className="absolute right-5 top-1/2 -translate-y-1/2 text-slate-400 text-xs font-bold">
                                    MÉDICOS SIMULTÁNEOS
                                </div>
                            </div>
                            <p className="text-[11px] text-slate-400 mt-1 ml-1 italic">
                                Define cuántos profesionales pueden agendar citas en el mismo bloque horario.
                            </p>
                        </div>

                        <div className="space-y-2">
                            <label className="flex items-center gap-2 text-xs font-bold text-slate-500 uppercase ml-1">
                                <FiClock className="text-slate-400" /> Tiempo entre Citas (Minutos)
                            </label>
                            <div className="relative group">
                                <select
                                    name="tiempo_calendarios"
                                    value={form.tiempo_calendarios}
                                    onChange={handleInputChange}
                                    className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-5 py-3.5 text-slate-700 font-medium focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all appearance-none cursor-pointer"
                                >
                                    <option value={15}>15 Minutos</option>
                                    <option value={20}>20 Minutos</option>
                                    <option value={30}>30 Minutos</option>
                                    <option value={45}>45 Minutos</option>
                                    <option value={60}>1 Hora</option>
                                    <option value={90}>1.5 Horas</option>
                                    <option value={120}>2 Horas</option>
                                </select>
                                <div className="absolute right-10 top-1/2 -translate-y-1/2 pointer-events-none">
                                    <FiClock className="text-slate-400" />
                                </div>
                            </div>
                            <p className="text-[11px] text-slate-400 mt-1 ml-1 italic">
                                Intervalo de tiempo predeterminado para cada turno en el calendario.
                            </p>
                        </div>
                    </div>
                </section>

                {/* Botón de Guardar */}
                <div className="flex justify-end pt-4">
                    <button
                        type="submit"
                        disabled={submitting}
                        className={`group px-10 py-4 rounded-2xl flex items-center justify-center gap-3 transition-all font-bold text-white shadow-xl ${
                            submitting
                            ? "bg-slate-400 cursor-not-allowed"
                            : "bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 hover:shadow-blue-500/25 active:scale-95"
                        }`}
                    >
                        {submitting ? (
                            <span className="inline-block w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                        ) : (
                            <FiCheckCircle className="w-5 h-5 group-hover:scale-110 transition-transform" />
                        )}
                        {submitting ? "Guardando Cambios..." : "Guardar Configuración"}
                    </button>
                </div>
            </form>

            <div className="mt-12 p-6 bg-blue-50/50 rounded-3xl border border-blue-100 flex gap-4">
                <div className="bg-blue-100 p-2 h-fit rounded-lg">
                    <FiInfo className="text-blue-600 w-5 h-5" />
                </div>
                <div className="space-y-1">
                    <h4 className="text-sm font-bold text-blue-900">Ayuda sobre Configuración</h4>
                    <p className="text-xs text-blue-700 leading-relaxed">
                        Estos ajustes afectan directamente el comportamiento de la pantalla de <strong>Citas Odontológicas</strong>. 
                        Asegúrate de coordinar los intervalos de tiempo con la disponibilidad de los consultorios físicos.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default ConfiguracionOdontologia;
